import React, { useState, useEffect } from 'react';
import AdminNav from '../components/AdminNav';
import { getVolunteers, getAssignments, getLocations, getShiftTimes } from '../services/firebase';
import './Admin.css';

const DAYS = ['Thu-April-9', 'Fri-April-10', 'Sat-April-11', 'Sun-April-12'];
const DAY_AVAIL_KEY = {
  'Thu-April-9':  'thuAvailability',
  'Fri-April-10': 'friAvailability',
  'Sat-April-11': 'satAvailability',
  'Sun-April-12': 'sunAvailability',
};

function availTotal(vol) {
  let n = 0;
  Object.values(DAY_AVAIL_KEY).forEach(k => {
    const v = vol[k];
    if (!v || v === '' || v === 'Not Available') return;
    if (v === 'Anytime') n += 5;
    else if (Array.isArray(v)) n += v.length;
    else n += 1;
  });
  return n;
}

function AvailPips({ vol }) {
  return (
    <div style={{ display: 'flex', gap: '3px', flexWrap: 'wrap' }}>
      {DAYS.map(day => {
        const avail = vol[DAY_AVAIL_KEY[day]];
        const label = day.split('-')[0];
        if (!avail || avail === '' || avail === 'Not Available')
          return <span key={day} className="avail-badge unavail" title="Unavailable">✗ {label}</span>;
        if (avail === 'Anytime')
          return <span key={day} className="avail-badge anytime" title="All day">{label}</span>;
        const count = Array.isArray(avail) ? avail.length : 1;
        return <span key={day} className="avail-badge partial" title={Array.isArray(avail) ? avail.join(', ') : avail}>{label} <small>{count}</small></span>;
      })}
    </div>
  );
}

function ShiftBadge({ count }) {
  const cls = count === 0 ? 'shift-count-badge zero'
            : count === 1 ? 'shift-count-badge one'
            : count === 2 ? 'shift-count-badge two'
            : 'shift-count-badge many';
  return <span className={cls}>{count} shift{count !== 1 ? 's' : ''}</span>;
}

function VolunteerLoad({ onLogout }) {
  const [volunteers, setVolunteers] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [locations, setLocations] = useState([]);
  const [shiftTimes, setShiftTimes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sortField, setSortField] = useState('shiftCount');
  const [sortDir, setSortDir] = useState('asc');
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [reportOpen, setReportOpen] = useState(false);

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    setLoading(true);
    const [vols, assigns, locs, shifts] = await Promise.all([
      getVolunteers(), getAssignments(), getLocations(), getShiftTimes(),
    ]);
    setVolunteers(vols);
    setAssignments(assigns);
    setLocations(locs.filter(l => l.active).map(l => l.name).sort());
    setShiftTimes(shifts.filter(s => s.active).sort((a, b) => a.order - b.order));
    setLoading(false);
  };

  // Build volunteerId → [{ day, location, shiftTime }]
  const volAssignMap = {};
  assignments.forEach(a => {
    (a.volunteers || []).forEach(id => {
      if (!id) return;
      if (!volAssignMap[id]) volAssignMap[id] = [];
      volAssignMap[id].push({ day: a.day, location: a.location, shiftTime: a.shiftTime });
    });
  });

  const totalAssignedVols = volunteers.filter(v => (volAssignMap[v.id] || []).length > 0).length;
  const totalShiftsAll = Object.values(volAssignMap).reduce((s, arr) => s + arr.length, 0);

  // Distinct congregations that have at least one volunteer with a shift assigned
  const congregationsWithShifts = new Set(
    volunteers
      .filter(v => (volAssignMap[v.id] || []).length > 0 && v.congregation)
      .map(v => v.congregation)
  ).size;

  // Activity report: total LOCAL slot-shifts available vs filled, broken down by day
  // A "local slot-shift" = one local slot (indices 3 & 4) × one shift time × one location
  // Total available local slots per day = locations × shiftTimes × 2 local slots
  const LOCAL_SLOTS_PER_SHIFT = 2;

  // Sum available and filled by day so the total is always consistent with per-day rows
  let totalLocalAvailable = 0;
  let totalLocalFilled = 0;
  const reportByDay = DAYS.map(day => {
    const dayShifts = shiftTimes.filter(s => !s.days || s.days.includes(day));
    const availableSlots = locations.length * dayShifts.length * LOCAL_SLOTS_PER_SHIFT;
    let filledSlots = 0;
    locations.forEach(loc => {
      dayShifts.forEach(st => {
        const a = assignments.find(x => x.day === day && x.location === loc && x.shiftTime === st.name);
        const vols = a?.volunteers || [];
        // Slots 3 & 4 are local slots
        if (vols[3]) filledSlots++;
        if (vols[4]) filledSlots++;
      });
    });
    totalLocalAvailable += availableSlots;
    totalLocalFilled += filledSlots;
    return { day, availableSlots, filledSlots };
  });

  let list = volunteers.map(v => ({
    ...v,
    shifts: (volAssignMap[v.id] || []).sort((a, b) => a.day.localeCompare(b.day) || a.shiftTime.localeCompare(b.shiftTime)),
    shiftCount: (volAssignMap[v.id] || []).length,
    availCount: availTotal(v),
  }));

  if (filter === 'assigned')   list = list.filter(v => v.shiftCount > 0);
  if (filter === 'unassigned') list = list.filter(v => v.shiftCount === 0);

  if (search.trim()) {
    const q = search.trim().toLowerCase();
    list = list.filter(v => `${v.firstName} ${v.lastName} ${v.congregation || ''}`.toLowerCase().includes(q));
  }

  const byName = (a, b) => (a.lastName || '').localeCompare(b.lastName || '') || (a.firstName || '').localeCompare(b.firstName || '');
  const handleSort = (field) => {
    if (field === sortField) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortField(field); setSortDir('asc'); }
  };
  const dir = sortDir === 'asc' ? 1 : -1;
  if (sortField === 'name')       list.sort((a, b) => dir * byName(a, b));
  if (sortField === 'congregation') list.sort((a, b) => dir * ((a.congregation || '').localeCompare(b.congregation || '')) || byName(a, b));
  if (sortField === 'shiftCount') list.sort((a, b) => dir * (a.shiftCount - b.shiftCount) || byName(a, b));
  if (sortField === 'availCount') list.sort((a, b) => dir * (a.availCount - b.availCount) || byName(a, b));

  if (loading) {
    return <div className="admin-container"><AdminNav onLogout={onLogout} /><div className="loading">Loading…</div></div>;
  }

  return (
    <div className="admin-container">
      <AdminNav onLogout={onLogout} />
      <header className="admin-header">
        <h1>Volunteer Load</h1>
        <p className="page-subtitle">Shift balance across all volunteers — use this to distribute assignments evenly.</p>
      </header>

      <div className="scheduling-content">
        {/* Stats */}
        <div className="stats-grid" style={{ marginBottom: '20px' }}>
          <div className="stat-card">
            <div className="stat-number">{volunteers.length}</div>
            <div className="stat-label">Total Volunteers</div>
          </div>
          <div className="stat-card info">
            <div className="stat-number">{totalAssignedVols}</div>
            <div className="stat-label">Have Shifts</div>
          </div>
          <div className="stat-card" style={{ borderLeft: '4px solid #dc2626' }}>
            <div className="stat-number" style={{ color: '#dc2626' }}>{volunteers.length - totalAssignedVols}</div>
            <div className="stat-label">Unassigned</div>
          </div>
          <div className="stat-card">
            <div className="stat-number">{totalShiftsAll}</div>
            <div className="stat-label">Total Shifts Filled</div>
          </div>
          <div className="stat-card">
            <div className="stat-number">{congregationsWithShifts}</div>
            <div className="stat-label">Congregations w/ Shifts</div>
            <div className="stat-hint">distinct congs. assigned</div>
          </div>
        </div>

        {/* Activity Report */}
        <div className="vol-load-report-panel" style={{ marginBottom: '24px' }}>
          <div
            className="vol-load-report-header"
            onClick={() => setReportOpen(o => !o)}
          >
            <span className="expand-icon">{reportOpen ? '▼' : '▶'}</span>
            <span>Activity Report — Local Shift Coverage</span>
            <span className="vol-load-report-summary">
              {totalLocalFilled} / {totalLocalAvailable} local slots filled
              {totalLocalAvailable > 0 && (
                <span style={{ marginLeft: 8, color: totalLocalFilled === totalLocalAvailable ? '#15803d' : '#92400e' }}>
                  ({Math.round(totalLocalFilled / totalLocalAvailable * 100)}%)
                </span>
              )}
            </span>
          </div>
          {reportOpen && (
            <div className="vol-load-report-body">
              <p className="vol-load-report-note">
                "Local slots" = the 2 local volunteer slots (indices 3 & 4) per shift × location.
                SMPW slots (1–3) are excluded. Only active locations and shift times are counted.
              </p>
              <table className="vol-load-report-table">
                <thead>
                  <tr>
                    <th>Day</th>
                    <th>Local Slots Available</th>
                    <th>Filled</th>
                    <th>Unfilled</th>
                    <th>Coverage</th>
                  </tr>
                </thead>
                <tbody>
                  {reportByDay.map(r => {
                    const pct = r.availableSlots > 0 ? Math.round(r.filledSlots / r.availableSlots * 100) : 0;
                    return (
                      <tr key={r.day}>
                        <td>{r.day.replace(/-/g, ' ')}</td>
                        <td>{r.availableSlots}</td>
                        <td style={{ color: '#15803d', fontWeight: 600 }}>{r.filledSlots}</td>
                        <td style={{ color: r.availableSlots - r.filledSlots > 0 ? '#b91c1c' : '#64748b', fontWeight: 600 }}>
                          {r.availableSlots - r.filledSlots}
                        </td>
                        <td>
                          <div className="vol-load-bar-wrap">
                            <div className="vol-load-bar-fill" style={{ width: `${pct}%` }} />
                            <span className="vol-load-bar-label">{pct}%</span>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                  <tr className="vol-load-report-total">
                    <td><strong>Total</strong></td>
                    <td><strong>{totalLocalAvailable}</strong></td>
                    <td style={{ color: '#15803d', fontWeight: 700 }}>{totalLocalFilled}</td>
                    <td style={{ color: totalLocalAvailable - totalLocalFilled > 0 ? '#b91c1c' : '#64748b', fontWeight: 700 }}>
                      {totalLocalAvailable - totalLocalFilled}
                    </td>
                    <td>
                      <div className="vol-load-bar-wrap">
                        <div className="vol-load-bar-fill" style={{ width: `${totalLocalAvailable > 0 ? Math.round(totalLocalFilled / totalLocalAvailable * 100) : 0}%` }} />
                        <span className="vol-load-bar-label">
                          {totalLocalAvailable > 0 ? Math.round(totalLocalFilled / totalLocalAvailable * 100) : 0}%
                        </span>
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Controls */}
        <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', marginBottom: '16px', alignItems: 'center' }}>
          <input
            type="text"
            placeholder="Search volunteers…"
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="search-input"
            style={{ maxWidth: '240px' }}
          />
          <div className="filter-pill-row" style={{ margin: 0 }}>
            {[
              { k: 'all',        l: `All (${volunteers.length})` },
              { k: 'assigned',   l: `Assigned (${totalAssignedVols})` },
              { k: 'unassigned', l: `Unassigned (${volunteers.length - totalAssignedVols})` },
            ].map(f => (
              <button key={f.k} className={`filter-pill ${filter === f.k ? 'active' : ''}`} onClick={() => setFilter(f.k)}>
                {f.l}
              </button>
            ))}
          </div>
        </div>

        {/* Table */}
        {list.length === 0 ? (
          <div className="no-data">No volunteers match.</div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table className="vol-load-table">
              <thead>
                <tr>
                  {[
                    { label: 'Name', field: 'name', count: list.length },
                    { label: 'Congregation', field: 'congregation', count: new Set(list.map(v => v.congregation).filter(Boolean)).size },
                    { label: 'Availability', field: 'availCount' },
                    { label: 'Shifts', field: 'shiftCount' },
                  ].map(col => (
                    <th
                      key={col.field}
                      className="vol-load-sort-th"
                      onClick={() => handleSort(col.field)}
                      style={{ cursor: 'pointer', userSelect: 'none' }}
                    >
                      {col.label}
                      {col.count != null && (
                        <span className="vol-load-col-count">{col.count}</span>
                      )}{' '}
                      {sortField === col.field
                        ? (sortDir === 'asc' ? '▲' : '▼')
                        : <span style={{ opacity: 0.3 }}>⇅</span>}
                    </th>
                  ))}
                  <th>Assigned To</th>
                </tr>
              </thead>
              <tbody>
                {list.map(vol => (
                  <tr key={vol.id} className={vol.shiftCount === 0 ? 'vol-load-row-unassigned' : ''}>
                    <td className="vol-load-name">
                      {vol.lastName}, {vol.firstName}
                      {vol.volunteerType === 'smpw' && <span className="roster-badge badge-smpw" style={{ marginLeft: 6 }}>SMPW</span>}
                      {vol.isKeyman && <span style={{ marginLeft: 4 }}>🔑</span>}
                      {vol.isDeafHH && <span className="roster-badge badge-deaf" title="Deaf / Hard of Hearing" style={{ marginLeft: 4 }}>🦻</span>}
                    </td>
                    <td style={{ fontSize: '13px', color: '#475569' }}>{vol.congregation || '—'}</td>
                    <td><AvailPips vol={vol} /></td>
                    <td><ShiftBadge count={vol.shiftCount} /></td>
                    <td>
                      {vol.shifts.length === 0 ? (
                        <span style={{ color: '#94a3b8', fontSize: '13px' }}>Not yet assigned</span>
                      ) : (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
                          {vol.shifts.map((s, i) => (
                            <span key={i} className="vol-load-shift-chip">
                              {s.day.split('-')[0]} · {s.location} · {s.shiftTime}
                            </span>
                          ))}
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

export default VolunteerLoad;
